import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
import { getApplianceType } from 'src/helpers/device_settings_helpers';
import { convertValueAndUnit } from 'src/helpers/utilities';
import type { Device } from 'src/types/Device';
import type { ConsumptionStores } from './consumption_helpers';
import { isEnergyMonitoringDemoMode } from './energy_data_mode';
import { DEMO_CONSUMERS } from './energy_demo_data';
import { buildLiveEnergySnapshot } from './live_energy_snapshot';

type BarColor =
	| 'primary'
	| 'success'
	| 'warning'
	| 'danger'
	| 'unknown'
	| 'roller'
	| 'lightning'
	| 'entertainment'
	| 'refrigeration'
	| 'laundry-cleaning'
	| 'cooking'
	| 'electric-vehicle'
	| 'ventilation'
	| 'water-heater'
	| 'other'
	| 'heating'
	| 'pool'
	| 'solar';

export type TopConsumerItem = {
	id: string;
	title: string;
	valueLabel: string;
	power: number;
	icon?: string;
	backgroundColor?: AvatarColorVariantTypes;
	image?: string;
	barColor: BarColor;
};

const formatPowerLabel = (power: number) => {
	const { value, unit } = convertValueAndUnit(power, 'W');
	return `${value} ${unit}`;
};

const demoBarColors: Record<string, BarColor> = {
	'demo-hvac': 'heating',
	'demo-ev': 'electric-vehicle',
	'demo-kitchen': 'cooking',
	'demo-laundry': 'laundry-cleaning',
	'demo-lighting': 'lightning',
};

export const buildTopConsumers = (
	stores: ConsumptionStores,
	devices: Record<string, Device>,
	limit = 5,
	groupBy: 'device' | 'room' = 'device',
	rooms: Record<string | number, { name?: string }> = {},
	useDemoData = isEnergyMonitoringDemoMode(),
): TopConsumerItem[] => {
	if (useDemoData) {
		return DEMO_CONSUMERS.map((item) => ({
			...item,
			barColor: demoBarColors[item.id] ?? 'other',
		})).slice(0, limit);
	}

	const live = buildLiveEnergySnapshot(stores, devices);
	const deviceList =
		groupBy === 'room'
			? Object.values(
					live.consumers.reduce<
						Record<
							string,
							{
								id: string;
								title: string;
								power: number;
							}
						>
					>((result, item) => {
						const roomId = String(item.roomId ?? 'unassigned');
						if (!result[roomId]) {
							result[roomId] = {
								id: `room-${roomId}`,
								title:
									rooms[item.roomId ?? '']?.name ??
									(item.roomId == null ? 'Unassigned' : `Room ${roomId}`),
								power: 0,
							};
						}
						result[roomId].power += item.power;
						return result;
					}, {}),
				)
					.sort((a, b) => b.power - a.power)
					.slice(0, limit)
			: live.consumers.slice(0, limit);

	if (!deviceList.length) {
		return [];
	}

	return deviceList.map((item) => {
		const device = groupBy === 'device' ? devices[item.id] : undefined;
		const applianceType = device ? getApplianceType(device) : null;
		const barColor = (applianceType?.color ?? 'other') as BarColor;

		return {
			id: String(item.id),
			title: item.title,
			valueLabel: formatPowerLabel(item.power),
			power: item.power,
			icon:
				(('icon' in item ? item.icon : undefined) as string | undefined) ??
				(applianceType ? `fa fa-${applianceType.icon}` : 'fa fa-door-open'),
			backgroundColor: ('backgroundColor' in item ? item.backgroundColor : undefined) as
				| AvatarColorVariantTypes
				| undefined,
			image: ('image' in item ? item.image : undefined) as string | undefined,
			barColor,
		};
	});
};
